/**
 * Telling a re-signed URL apart from a different one.
 *
 * The live status endpoint signs the playlist and thumbnail URLs afresh on
 * every poll, so the URL differs each time even though it names the same
 * resource. Treating each signing as new rebuilds playback and re-downloads
 * artwork every poll.
 */

/**
 * How long before a signed source expires the player swaps to the fresh one
 * the status poll brought. Well over the player's poll interval; a token that
 * lapses anyway (a slow custom interval, a viewer clock running behind) is
 * recovered from the playlist 403 instead.
 */
export const SIGNED_SOURCE_REFRESH_MARGIN_MS = 10 * 60 * 1000;

// The query form of a signed URL; the path form carries the same names in its
// first path segment.
const QUERY_TOKEN_PARAMS = ['token', 'expires'];

type SplitSource = {
  /** The URL without its signature, naming the signed resource. */
  identity: string;
  /** When the signature stops being accepted, in epoch ms. */
  expiresAt: number | null;
};

/**
 * Bunny puts a path token in the first path segment
 * (`/bcdn_token=…&expires=…&token_path=…/live/…/live.m3u8`); a query token
 * sits in `?token=…&expires=…`. The server reads the path form the same way:
 * keep this in step with `extractCdnTokenParams` / `removeCdnToken` in
 * `src/services/video-client.ts` (the client cannot import from `src/`).
 */
function splitSignedSource(value: string): SplitSource {
  let url: URL;
  try {
    url = new URL(value, globalThis.location?.href);
  } catch {
    return { identity: value, expiresAt: null };
  }

  const segments = url.pathname.split('/');
  const pathToken = segments[1]?.startsWith('bcdn_token=')
    ? new URLSearchParams(segments[1])
    : null;

  const rawExpiry = (pathToken ?? url.searchParams).get('expires');
  const seconds = rawExpiry ? Number(rawExpiry) : NaN;
  const expiresAt = Number.isFinite(seconds) && seconds > 0
    ? seconds * 1000
    : null;

  if (pathToken) {
    url.pathname = '/' + segments.slice(2).join('/');
  } else {
    QUERY_TOKEN_PARAMS.forEach((name) => url.searchParams.delete(name));
  }

  return { identity: url.toString(), expiresAt };
}

/**
 * Whether two URLs name the same resource, ignoring how they were signed. Two
 * missing URLs are the same; a missing and a present one are not.
 */
export function isSameSignedSource(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  if (!a || !b) return !a && !b;
  if (a === b) return true;
  return splitSignedSource(a).identity === splitSignedSource(b).identity;
}

/** Whether a signed URL's token lapses within the refresh margin. */
export function isSignedSourceExpiring(
  value: string | null | undefined,
  nowMs: number = Date.now(),
): boolean {
  if (!value) return false;
  const { expiresAt } = splitSignedSource(value);
  return expiresAt !== null &&
    expiresAt - nowMs <= SIGNED_SOURCE_REFRESH_MARGIN_MS;
}
